// frontend/src/components/SupplierInvoices/SupplierInvoiceTable.jsx
import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, FileText, ExternalLink, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const STATUS_STYLES = {
  received:  'bg-blue-500/20   text-blue-400   border border-blue-500/30',
  pending:   'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
  paid:      'bg-green-500/20  text-green-400  border border-green-500/30',
  disputed:  'bg-orange-500/20 text-orange-400 border border-orange-500/30',
  cancelled: 'bg-gray-500/20   text-gray-400   border border-gray-500/30',
};

const TYPE_LABELS = {
  purchase:         'Purchase',
  recurring_charge: 'Recurring',
};

const fmt = (val) =>
  val != null ? `$${parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// ── Hook détection mobile ─────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// ── Status Dropdown (PC/Mac — hover) ─────────────────────────────────────────
const StatusDropdown = ({ invoice, onChange }) => {
  const statuses = ['received', 'pending', 'paid', 'disputed', 'cancelled'];
  return (
    <div className="relative group">
      <button className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${STATUS_STYLES[invoice.status] || ''}`}>
        {invoice.status}
        <ChevronDown size={10} />
      </button>
      <div className="absolute left-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10 hidden group-hover:block min-w-[130px]">
        {statuses.map(s => (
          <button
            key={s}
            onClick={() => onChange(invoice.id, s)}
            className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-700 transition-colors ${s === invoice.status ? 'text-white font-medium' : 'text-gray-400'}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Status Select (Mobile — natif tactile) ────────────────────────────────────
const StatusSelect = ({ invoice, onChange }) => {
  const statuses = ['received', 'pending', 'paid', 'disputed', 'cancelled'];
  return (
    <select
      value={invoice.status}
      onChange={e => onChange(invoice.id, e.target.value)}
      className={`rounded-full text-xs font-medium px-2 py-1 border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-indigo-500 ${STATUS_STYLES[invoice.status] || ''} bg-transparent`}
    >
      {statuses.map(s => (
        <option key={s} value={s} className="bg-gray-800 text-white">
          {s.charAt(0).toUpperCase() + s.slice(1)}
        </option>
      ))}
    </select>
  );
};

// ── Vue Mobile : Cards ────────────────────────────────────────────────────────
const MobileCardList = ({ invoices, onEdit, onDelete, onStatusChange, canDelete }) => {
  const openPdf = (invoiceId) => {
    window.open(`${API_BASE}/supplier-invoices/${invoiceId}/pdf`, '_blank');
  };

  return (
    <div className="space-y-3">
      {invoices.map(inv => {
        const isOverdue = inv.status === 'pending' && inv.due_date && new Date(inv.due_date) < new Date();
        const totalTax = parseFloat(inv.tax_state_amount || 0) + parseFloat(inv.tax_airport_amount || 0);

        return (
          <div key={inv.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 space-y-3">

            {/* Ligne 1 : Invoice # + Total */}
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white font-mono text-xs font-semibold">{inv.invoice_number}</p>
                {inv.supplier_ref && (
                  <p className="text-gray-500 text-xs mt-0.5">Ref: {inv.supplier_ref}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-white font-semibold text-base">{fmt(inv.total_amount)}</p>
                {totalTax > 0 && (
                  <p className="text-gray-500 text-xs">Tax: {fmt(totalTax)}</p>
                )}
              </div>
            </div>

            {/* Ligne 2 : Supplier + Type */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm">{inv.supplier_name || '—'}</p>
                {inv.supplier_category && (
                  <p className="text-gray-500 text-xs">{inv.supplier_category}</p>
                )}
              </div>
              <span className={`inline-flex px-2 py-0.5 rounded text-xs ${
                inv.invoice_type === 'recurring_charge'
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'bg-gray-500/20 text-gray-400'
              }`}>
                {TYPE_LABELS[inv.invoice_type] || inv.invoice_type}
              </span>
            </div>

            {/* Ligne 3 : Dates */}
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span>📅 {fmtDate(inv.invoice_date)}</span>
              {inv.due_date && (
                <span className={isOverdue ? 'text-red-400 font-medium' : ''}>
                  ⏰ {fmtDate(inv.due_date)}{isOverdue && ' ⚠'}
                </span>
              )}
            </div>

            {/* Ligne 4 : Status + Actions */}
            <div className="flex items-center justify-between pt-1 border-t border-gray-700/50">
              <StatusSelect invoice={inv} onChange={onStatusChange} />

              <div className="flex items-center gap-2">
                {/* PDF */}
                {inv.attachment_path && (
                  <button
                    onClick={() => openPdf(inv.id)}
                    className="flex items-center gap-1 px-2 py-1.5 bg-indigo-500/20 hover:bg-indigo-500/40 text-indigo-400 rounded-lg text-xs transition-colors"
                    title="View PDF"
                  >
                    <FileText size={13} />
                    <ExternalLink size={11} />
                  </button>
                )}

                {/* Edit */}
                <button
                  onClick={() => onEdit(inv)}
                  className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit2 size={15} />
                </button>

                {/* Delete */}
                {canDelete && (
                  <button
                    onClick={() => onDelete(inv.id)}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Footer compteur */}
      <p className="text-gray-500 text-xs px-1">
        {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
      </p>
    </div>
  );
};

// ── Vue Desktop : Table (inchangée) ──────────────────────────────────────────
const DesktopTable = ({ invoices, onEdit, onDelete, onStatusChange, canDelete }) => {
  const openPdf = (invoiceId) => {
    window.open(`${API_BASE}/supplier-invoices/${invoiceId}/pdf`, '_blank');
  };

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700 bg-gray-900/40">
              <th className="text-left text-gray-400 font-medium px-4 py-3">Invoice #</th>
              <th className="text-left text-gray-400 font-medium px-4 py-3">Supplier</th>
              <th className="text-left text-gray-400 font-medium px-4 py-3 hidden md:table-cell">Type</th>
              <th className="text-left text-gray-400 font-medium px-4 py-3 hidden md:table-cell">Date</th>
              <th className="text-left text-gray-400 font-medium px-4 py-3 hidden lg:table-cell">Due Date</th>
              <th className="text-right text-gray-400 font-medium px-4 py-3">Total</th>
              <th className="text-left text-gray-400 font-medium px-4 py-3">Status</th>
              <th className="text-center text-gray-400 font-medium px-4 py-3 hidden md:table-cell">PDF</th>
              <th className="text-right text-gray-400 font-medium px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {invoices.map(inv => {
              const isOverdue = inv.status === 'pending' && inv.due_date && new Date(inv.due_date) < new Date();
              return (
                <tr key={inv.id} className="hover:bg-gray-700/20 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-white font-mono text-xs">{inv.invoice_number}</p>
                    {inv.supplier_ref && (
                      <p className="text-gray-500 text-xs">Ref: {inv.supplier_ref}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-white text-sm">{inv.supplier_name || '—'}</p>
                    {inv.supplier_category && (
                      <p className="text-gray-500 text-xs">{inv.supplier_category}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs ${
                      inv.invoice_type === 'recurring_charge'
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {TYPE_LABELS[inv.invoice_type] || inv.invoice_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-gray-300 text-xs">{fmtDate(inv.invoice_date)}</p>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <p className={`text-xs ${isOverdue ? 'text-red-400 font-medium' : 'text-gray-300'}`}>
                      {fmtDate(inv.due_date)}
                      {isOverdue && <span className="ml-1">⚠</span>}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className="text-white font-medium">{fmt(inv.total_amount)}</p>
                    {parseFloat(inv.tax_state_amount || 0) + parseFloat(inv.tax_airport_amount || 0) > 0 && (
                      <p className="text-gray-500 text-xs">
                        Tax: {fmt(parseFloat(inv.tax_state_amount || 0) + parseFloat(inv.tax_airport_amount || 0))}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusDropdown invoice={inv} onChange={onStatusChange} />
                  </td>
                  <td className="px-4 py-3 text-center hidden md:table-cell">
                    {inv.attachment_path ? (
                      <button
                        onClick={() => openPdf(inv.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-500/20 hover:bg-indigo-500/40 text-indigo-400 rounded text-xs transition-colors"
                        title="View PDF"
                      >
                        <FileText size={11} />
                        <ExternalLink size={10} />
                      </button>
                    ) : (
                      <span className="text-gray-600 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onEdit(inv)}
                        className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => onDelete(inv.id)}
                          className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 border-t border-gray-700 bg-gray-900/20">
        <p className="text-gray-500 text-xs">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</p>
      </div>
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────
const SupplierInvoiceTable = ({ invoices, loading, onEdit, onDelete, onStatusChange }) => {
  const { user } = useAuth();
  const userLevel = user?.permissions?.level ?? 0;
  const canDelete = [90, 100, 110].includes(userLevel);
  const isMobile = useIsMobile();

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (!invoices.length) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 text-center">
        <FileText size={32} className="text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400 text-sm">No supplier invoices found.</p>
      </div>
    );
  }

  return isMobile
    ? <MobileCardList
        invoices={invoices}
        onEdit={onEdit}
        onDelete={onDelete}
        onStatusChange={onStatusChange}
        canDelete={canDelete}
      />
    : <DesktopTable
        invoices={invoices}
        onEdit={onEdit}
        onDelete={onDelete}
        onStatusChange={onStatusChange}
        canDelete={canDelete}
      />;
};

export default SupplierInvoiceTable;